import { useEffect, useRef, useState } from "react";
import {
  avviaDettaglio,
  getAnalisi,
  getAnalisiSessione,
  type Analisi,
  type AnalisiSessione,
  type Dettaglio,
  type RisultatoAnalisi,
  type StatoAnalisi,
  type StatoDettaglio,
} from "@/api/analisiBolletta";

export const POLL_MS = 1_000;
export const SCADENZA_MS = 3 * 60 * 1000;
/** Ruling 73: la fase 2 (il gas fino a 180 s, poi la formula) si aspetta 5 minuti, contati dal suo inizio. */
export const SCADENZA_FASE2_MS = 5 * 60 * 1000;

export type StatoVoce = StatoAnalisi | "scaduta";

export interface VoceAnalisi {
  id: string;
  stato: StatoVoce;
  risultato: RisultatoAnalisi | null;
  /** fase 2 (voci da segnalare): null = non prevista per questo operatore */
  dettaglioStato?: StatoDettaglio | null;
  dettaglio?: Dettaglio | null;
  /** avvia la fase 2 (su richiesta, o di nuovo dopo un errore) */
  controlla?: () => void;
}

function voce(id: string, a: Analisi): VoceAnalisi {
  return { id, stato: a.stato, risultato: a.risultato, dettaglioStato: a.dettaglio_stato ?? null,
    dettaglio: a.dettaglio ?? null };
}

const inLavoro = (a: Analisi) => a.stato === "in_corso" || a.dettaglio_stato === "in_corso";

/**
 * Stato delle analisi di bolletta della chat. Con `ids` interroga ogni analisi ogni secondo
 * finché è «pronta» o «errore» e la fase 2, se prevista, ha finito (la fase 1 ancora in corso
 * dopo 3 minuti diventa «scaduta»; la fase 2 ancora in corso 5 minuti dopo il suo inizio diventa
 * «errore», senza toccare il risultato della fase 1). Senza id, una sola volta, ricostruisce dalla sessione
 * (ricaricamento della pagina); risposta vuota o errore = nessuna voce. `ripristina:false`
 * (clienti) = nessuna richiesta. Quando `azzera` cambia (nuova consulenza) la lista ripristinata
 * non vale più. L'ordine è quello degli id (ordine di caricamento).
 */
/** Da dove si leggono le analisi: di base le richieste dello staff; il percorso QR passa le sue, legate alla
 *  sessione firmata del cliente (senza `avvia`: lì la fase 2 parte da sola). */
export interface FonteAnalisi {
  leggi: (id: string) => Promise<Analisi>;
  sessione: () => Promise<AnalisiSessione[]>;
  avvia?: (id: string) => Promise<unknown>;
}

// funzioni chiamate al momento, non lette all'avvio del modulo
const FONTE_STAFF: FonteAnalisi = {
  leggi: (id) => getAnalisi(id), sessione: () => getAnalisiSessione(), avvia: (id) => avviaDettaglio(id),
};

export function useAnalisiChat(
  ids: string[],
  { ripristina = true, azzera = 0, fonte = FONTE_STAFF }: { ripristina?: boolean; azzera?: number; fonte?: FonteAnalisi } = {},
): VoceAnalisi[] {
  const fonteRef = useRef(fonte);
  fonteRef.current = fonte;
  const [voci, setVoci] = useState<Record<string, VoceAnalisi>>({});
  const [sessione, setSessione] = useState<{ ordine: string[]; gen: number }>({ ordine: [], gen: azzera });
  const azzeraRef = useRef(azzera);
  azzeraRef.current = azzera;
  const seguiti = useRef<Set<string>>(new Set());      // analisi già prese in carico: non si ricominciano
  const attivi = useRef<Set<string>>(new Set());    // analisi che si stanno interrogando ora
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const attivo = useRef(true);

  const imposta = (v: VoceAnalisi) => setVoci((prev) => ({ ...prev, [v.id]: v }));
  const aggiorna = (id: string, campi: Partial<VoceAnalisi>) =>
    setVoci((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...campi } } : prev));

  /** Interroga l'analisi finché qualcosa è in corso (o fino alla scadenza). */
  const interroga = (id: string) => {
    let fine = Date.now() + SCADENZA_MS;
    let fase2 = false;         // la scadenza della fase 2 si conta dal primo giro che la vede in corso
    let ultima: Analisi | null = null;
    const giro = async () => {
      if (!attivo.current) return;
      try {
        const a = await fonteRef.current.leggi(id);
        if (!attivo.current) return;
        ultima = a;
        if (!fase2 && a.stato !== "in_corso" && a.dettaglio_stato === "in_corso") {
          fase2 = true;
          fine = Date.now() + SCADENZA_FASE2_MS;
        }
        imposta(voce(id, a));
        if (!inLavoro(a)) {
          attivi.current.delete(id);
          return;
        }
      } catch {
        // rete o server: si riprova al giro dopo, fino alla scadenza
      }
      if (Date.now() >= fine) {
        attivi.current.delete(id);
        if (ultima && ultima.stato !== "in_corso") aggiorna(id, { dettaglioStato: "errore" });
        else imposta({ id, stato: "scaduta", risultato: null, dettaglioStato: null, dettaglio: null });
        return;
      }
      const t = setTimeout(() => { timers.current.delete(t); void giro(); }, POLL_MS);
      timers.current.add(t);
    };
    void giro();
  };

  const segui = (id: string) => {
    if (seguiti.current.has(id)) return;
    seguiti.current.add(id);
    attivi.current.add(id);
    imposta({ id, stato: "in_corso", risultato: null, dettaglioStato: null, dettaglio: null });
    interroga(id);
  };

  const controlla = (id: string) => {
    if (attivi.current.has(id)) return;
    attivi.current.add(id);
    aggiorna(id, { dettaglioStato: "in_corso", dettaglio: null });
    const avvia = fonteRef.current.avvia;
    if (!avvia) return;
    avvia(id)
      .then(() => interroga(id))
      .catch(() => {
        attivi.current.delete(id);
        aggiorna(id, { dettaglioStato: "errore" });
      });
  };

  const chiave = ids.join("|");
  useEffect(() => {
    attivo.current = true;
    ids.forEach(segui);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiave]);

  // Ricostruzione dalla sessione: solo al montaggio e solo senza id.
  useEffect(() => {
    if (ids.length > 0 || !ripristina) return;
    const gen = azzeraRef.current;
    // mai un errore che fermi la chat: una lettura che non risponde (o non c'è) = nessuna voce
    Promise.resolve().then(() => fonteRef.current.sessione())
      .then((lista) => {
        if (!attivo.current || !Array.isArray(lista)) return;
        const nuove: Record<string, VoceAnalisi> = {};
        for (const a of lista) nuove[a.analisi_id] = voce(a.analisi_id, a);
        setVoci((prev) => ({ ...nuove, ...prev }));
        setSessione({ ordine: lista.map((a) => a.analisi_id), gen });
        lista.forEach((a) => seguiti.current.add(a.analisi_id));
        lista.filter(inLavoro).forEach((a) => {
          if (attivi.current.has(a.analisi_id)) return;
          attivi.current.add(a.analisi_id);
          interroga(a.analisi_id);
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = timers.current;
    const a = seguiti.current;
    const l = attivi.current;
    return () => {
      attivo.current = false;
      t.forEach(clearTimeout);
      t.clear();
      a.clear();
      l.clear();
    };
  }, []);

  const ordine = ids.length > 0 ? ids : sessione.gen === azzera ? sessione.ordine : [];
  return ordine
    .map((id) => voci[id])
    .filter((v): v is VoceAnalisi => Boolean(v))
    .map((v) => ({ ...v, controlla: fonte.avvia ? () => controlla(v.id) : undefined }));
}
